import React, { useEffect, useState } from 'react'
import { backendService, type FarmRegistrationCreate } from '../../services/backendService'
import { logError, logInfo } from '../../utils/errorLogger'
import './DoctorRegistration.css'

interface FormData {
  businessName: string
  ownerName: string
  username: string
  password: string
  confirmPassword: string
  email: string
  phone: string
  website: string
  description: string
  addressLine1: string
  city: string
  stateProvince: string
  postalCode: string
  country: string
  printerCount: string
  materialsAvailable: string
  medicalGradeCapable: boolean
}

interface ValidationErrors {
  businessName?: string
  ownerName?: string
  username?: string
  password?: string
  confirmPassword?: string
  email?: string
  addressLine1?: string
  city?: string
  country?: string
}

const initialFormData: FormData = {
  businessName: '',
  ownerName: '',
  username: '',
  password: '',
  confirmPassword: '',
  email: '',
  phone: '',
  website: '',
  description: '',
  addressLine1: '',
  city: '',
  stateProvince: '',
  postalCode: '',
  country: 'PT',
  printerCount: '1',
  materialsAvailable: 'PLA',
  medicalGradeCapable: false
}

export const FarmRegistration: React.FC = () => {
  const [formData, setFormData] = useState<FormData>(initialFormData)
  const [errors, setErrors] = useState<ValidationErrors>({})
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitSuccess, setSubmitSuccess] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)

  useEffect(() => {
    try {
      const saved = sessionStorage.getItem('farmRegistrationDraft')
      if (saved) {
        const parsed = JSON.parse(saved)
        setFormData(prev => ({ ...prev, ...parsed }))
        logInfo('Loaded farm registration draft from session storage', 'FarmRegistration')
      }
    } catch {
      logError('Failed to load draft from session storage', 'FarmRegistration')
    }
  }, [])

  useEffect(() => {
    try {
      const draftData = { ...formData, password: '', confirmPassword: '' }
      sessionStorage.setItem('farmRegistrationDraft', JSON.stringify(draftData))
    } catch {
      logError('Failed to save draft to session storage', 'FarmRegistration')
    }
  }, [formData])

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const target = e.target
    const value = target instanceof HTMLInputElement && target.type === 'checkbox' ? target.checked : target.value
    const name = target.name as keyof FormData
    setFormData(prev => ({ ...prev, [name]: value as never }))

    if (errors[name as keyof ValidationErrors]) {
      setErrors(prev => ({ ...prev, [name]: undefined }))
    }

    if (submitError) {
      setSubmitError(null)
    }
  }

  const validateForm = () => {
    const nextErrors: ValidationErrors = {}

    if (!formData.businessName.trim()) nextErrors.businessName = 'Business name is required'
    if (!formData.ownerName.trim()) nextErrors.ownerName = 'Owner name is required'
    if (!formData.username.trim()) nextErrors.username = 'Username is required'
    if (!formData.password) nextErrors.password = 'Password is required'
    else if (formData.password.length < 8) nextErrors.password = 'Password must be at least 8 characters'
    else if (!/[A-Z]/.test(formData.password)) nextErrors.password = 'Password must contain at least one uppercase letter'
    else if (!/[0-9]/.test(formData.password)) nextErrors.password = 'Password must contain at least one number'
    if (!formData.confirmPassword) nextErrors.confirmPassword = 'Please confirm your password'
    else if (formData.password !== formData.confirmPassword) nextErrors.confirmPassword = 'Passwords do not match'
    if (!formData.email.trim()) nextErrors.email = 'Email is required'
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) nextErrors.email = 'Please enter a valid email address'
    if (!formData.addressLine1.trim()) nextErrors.addressLine1 = 'Address line 1 is required'
    if (!formData.city.trim()) nextErrors.city = 'City is required'
    if (!formData.country.trim()) nextErrors.country = 'Country is required'

    setErrors(nextErrors)
    return Object.keys(nextErrors).length === 0
  }

  const resetForm = () => {
    setFormData(initialFormData)
    setErrors({})
    setSubmitError(null)
    sessionStorage.removeItem('farmRegistrationDraft')
    logInfo('Farm registration form reset', 'FarmRegistration')
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!validateForm()) {
      logError('Form validation failed', 'FarmRegistration')
      return
    }

    setIsSubmitting(true)
    setSubmitError(null)
    setSubmitSuccess(false)

    try {
      const farmData: FarmRegistrationCreate = {
        businessName: formData.businessName.trim(),
        ownerName: formData.ownerName.trim(),
        email: formData.email.trim(),
        phone: formData.phone.trim() || undefined,
        website: formData.website.trim() || undefined,
        description: formData.description.trim() || undefined,
        credentials: {
          username: formData.username.trim(),
          password: formData.password
        },
        address: {
          line1: formData.addressLine1.trim(),
          city: formData.city.trim(),
          stateProvince: formData.stateProvince.trim() || undefined,
          postalCode: formData.postalCode.trim() || undefined,
          country: formData.country.trim().toUpperCase()
        },
        capabilities: {
          printerCount: Number.parseInt(formData.printerCount, 10) || 1,
          materialsAvailable: formData.materialsAvailable.split(',').map(v => v.trim()).filter(Boolean),
          medicalGradeCapable: formData.medicalGradeCapable
        }
      }

      const result = await backendService.registerFarm(farmData)
      logInfo(`Farm registered successfully: ${result.businessName} (ID: ${result.farmId})`, 'FarmRegistration')
      setSubmitSuccess(true)

      setTimeout(() => {
        resetForm()
        setSubmitSuccess(false)
      }, 3000)
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to register farm'
      logError(`Farm registration failed: ${errorMessage}`, 'FarmRegistration')
      setSubmitError(errorMessage)
      setSubmitSuccess(false)
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="doctor-registration">
      <form onSubmit={handleSubmit} className="doctor-registration-form">
        {submitSuccess && (
          <div className="alert alert-success">
            <span className="alert-icon">✅</span>
            <div className="alert-content">
              <strong>Success!</strong> Farm registered successfully.
            </div>
          </div>
        )}

        {submitError && (
          <div className="alert alert-error">
            <span className="alert-icon">❌</span>
            <div className="alert-content">
              <strong>Error:</strong> {submitError}
            </div>
          </div>
        )}

        <div className="form-section">
          <h3 className="form-section-title">Business Information</h3>
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="businessName" className="form-label">Business Name <span className="required">*</span></label>
              <input id="businessName" name="businessName" type="text" className={`form-input ${errors.businessName ? 'error' : ''}`} value={formData.businessName} onChange={handleInputChange} disabled={isSubmitting} autoFocus />
              {errors.businessName && <span className="error-message">{errors.businessName}</span>}
            </div>
            <div className="form-group">
              <label htmlFor="ownerName" className="form-label">Owner Name <span className="required">*</span></label>
              <input id="ownerName" name="ownerName" type="text" className={`form-input ${errors.ownerName ? 'error' : ''}`} value={formData.ownerName} onChange={handleInputChange} disabled={isSubmitting} />
              {errors.ownerName && <span className="error-message">{errors.ownerName}</span>}
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="email" className="form-label">Email <span className="required">*</span></label>
              <input id="email" name="email" type="email" className={`form-input ${errors.email ? 'error' : ''}`} value={formData.email} onChange={handleInputChange} disabled={isSubmitting} />
              {errors.email && <span className="error-message">{errors.email}</span>}
            </div>
            <div className="form-group">
              <label htmlFor="phone" className="form-label">Phone</label>
              <input id="phone" name="phone" type="text" className="form-input" value={formData.phone} onChange={handleInputChange} disabled={isSubmitting} />
            </div>
          </div>
          <div className="form-group">
            <label htmlFor="website" className="form-label">Website</label>
            <input id="website" name="website" type="url" className="form-input" value={formData.website} onChange={handleInputChange} disabled={isSubmitting} />
          </div>
          <div className="form-group">
            <label htmlFor="description" className="form-label">Description</label>
            <textarea id="description" name="description" className="form-input" value={formData.description} onChange={handleInputChange} disabled={isSubmitting} rows={3} />
          </div>
        </div>

        <div className="form-section">
          <h3 className="form-section-title">Account Credentials</h3>
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="username" className="form-label">Username <span className="required">*</span></label>
              <input id="username" name="username" type="text" className={`form-input ${errors.username ? 'error' : ''}`} value={formData.username} onChange={handleInputChange} disabled={isSubmitting} />
              {errors.username && <span className="error-message">{errors.username}</span>}
            </div>
            <div className="form-group">
              <label htmlFor="password" className="form-label">Password <span className="required">*</span></label>
              <input id="password" name="password" type="password" className={`form-input ${errors.password ? 'error' : ''}`} value={formData.password} onChange={handleInputChange} disabled={isSubmitting} />
              {errors.password && <span className="error-message">{errors.password}</span>}
            </div>
          </div>
          <div className="form-group">
            <label htmlFor="confirmPassword" className="form-label">Confirm Password <span className="required">*</span></label>
            <input id="confirmPassword" name="confirmPassword" type="password" className={`form-input ${errors.confirmPassword ? 'error' : ''}`} value={formData.confirmPassword} onChange={handleInputChange} disabled={isSubmitting} />
            {errors.confirmPassword && <span className="error-message">{errors.confirmPassword}</span>}
          </div>
        </div>

        <div className="form-section">
          <h3 className="form-section-title">Address & Capabilities</h3>
          <div className="form-group">
            <label htmlFor="addressLine1" className="form-label">Address Line 1 <span className="required">*</span></label>
            <input id="addressLine1" name="addressLine1" type="text" className={`form-input ${errors.addressLine1 ? 'error' : ''}`} value={formData.addressLine1} onChange={handleInputChange} disabled={isSubmitting} />
            {errors.addressLine1 && <span className="error-message">{errors.addressLine1}</span>}
          </div>
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="city" className="form-label">City <span className="required">*</span></label>
              <input id="city" name="city" type="text" className={`form-input ${errors.city ? 'error' : ''}`} value={formData.city} onChange={handleInputChange} disabled={isSubmitting} />
              {errors.city && <span className="error-message">{errors.city}</span>}
            </div>
            <div className="form-group">
              <label htmlFor="country" className="form-label">Country Code <span className="required">*</span></label>
              <input id="country" name="country" type="text" maxLength={2} className={`form-input ${errors.country ? 'error' : ''}`} value={formData.country} onChange={handleInputChange} disabled={isSubmitting} />
              {errors.country && <span className="error-message">{errors.country}</span>}
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="stateProvince" className="form-label">State / Province</label>
              <input id="stateProvince" name="stateProvince" type="text" className="form-input" value={formData.stateProvince} onChange={handleInputChange} disabled={isSubmitting} />
            </div>
            <div className="form-group">
              <label htmlFor="postalCode" className="form-label">Postal Code</label>
              <input id="postalCode" name="postalCode" type="text" className="form-input" value={formData.postalCode} onChange={handleInputChange} disabled={isSubmitting} />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="printerCount" className="form-label">Printer Count</label>
              <input id="printerCount" name="printerCount" type="number" min={1} className="form-input" value={formData.printerCount} onChange={handleInputChange} disabled={isSubmitting} />
            </div>
            <div className="form-group">
              <label htmlFor="materialsAvailable" className="form-label">Materials Available</label>
              <input id="materialsAvailable" name="materialsAvailable" type="text" className="form-input" value={formData.materialsAvailable} onChange={handleInputChange} disabled={isSubmitting} />
              <span className="form-hint">Comma-separated, e.g. PLA, TPU</span>
            </div>
          </div>
          <div className="form-group">
            <label className="form-label" htmlFor="medicalGradeCapable">
              <input id="medicalGradeCapable" name="medicalGradeCapable" type="checkbox" checked={formData.medicalGradeCapable} onChange={handleInputChange} disabled={isSubmitting} style={{ marginRight: '8px', width: 'auto' }} />
              Medical-grade capable
            </label>
          </div>
        </div>

        <div className="form-actions">
          <button type="button" className="btn btn-secondary" onClick={resetForm} disabled={isSubmitting}>Reset Form</button>
          <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
            {isSubmitting ? <><span className="spinner"></span> Registering...</> : 'Register Farm'}
          </button>
        </div>
      </form>
    </div>
  )
}